import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Mail, Send, RefreshCw, Inbox, Search, X, ChevronLeft, AlertCircle,
  CheckCircle2, Loader2, MailOpen, Reply, ExternalLink, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('crm_token')}` });

const FOLDERS = [
  { id: 'INBOX', label: 'Inbox', icon: Inbox },
  { id: 'STARRED', label: 'Starred', icon: Mail },
  { id: 'SENT', label: 'Sent', icon: Send },
  { id: 'DRAFT', label: 'Drafts', icon: Mail },
  { id: 'TRASH', label: 'Trash', icon: Trash2 },
];

const formatDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    const now = new Date();
    const sameDay = dt.toDateString() === now.toDateString();
    if (sameDay) return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return dt.toLocaleDateString([], { day: '2-digit', month: 'short' });
  } catch { return d; }
};

const parseFrom = (s) => {
  if (!s) return { name: '', email: '' };
  const m = s.match(/^(.*?)<(.+?)>$/);
  if (m) return { name: m[1].replace(/"/g, '').trim(), email: m[2].trim() };
  return { name: s.split('@')[0], email: s };
};

// ─── CONNECT PROMPT ─────────────────────────────────────────────────────────
const ConnectPrompt = ({ onConnect, configured }) => (
  <div className="flex flex-col items-center justify-center py-20 px-6 text-center" data-testid="gmail-connect-prompt">
    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center mb-6 shadow-lg">
      <Mail className="w-10 h-10 text-white" />
    </div>
    <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
      Connect your Google Workspace inbox
    </h2>
    <p className="text-slate-500 max-w-md mb-6">
      Read, compose and reply to your <strong>@siddhivinayakinfra.estate</strong> emails right
      inside DBK CRM — no need to flip to Gmail. Your messages never leave Google.
    </p>
    {!configured ? (
      <div className="max-w-md p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3 text-left">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-900">Admin setup required</p>
          <p className="text-xs text-amber-700 mt-1">
            Gmail API credentials are not configured yet. Your administrator needs to add
            <code className="mx-1 px-1 bg-amber-100 rounded">GMAIL_CLIENT_ID</code> and
            <code className="mx-1 px-1 bg-amber-100 rounded">GMAIL_CLIENT_SECRET</code>
            to the backend. See <code>/app/GOOGLE_GMAIL_SETUP.md</code>.
          </p>
        </div>
      </div>
    ) : (
      <button
        onClick={onConnect}
        data-testid="gmail-connect-btn"
        className="flex items-center gap-3 bg-white border border-slate-300 hover:border-slate-400 px-6 py-3 rounded-lg shadow-sm hover:shadow transition-all font-semibold text-slate-700"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.32z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
        </svg>
        Connect Gmail
      </button>
    )}
  </div>
);

// ─── COMPOSE MODAL ──────────────────────────────────────────────────────────
const ComposeModal = ({ open, onClose, initial = {}, onSent }) => {
  const [form, setForm] = useState({ to: '', cc: '', subject: '', body: '', ...initial });
  const [sending, setSending] = useState(false);

  useEffect(() => { if (open) setForm({ to: '', cc: '', subject: '', body: '', ...initial }); }, [open]); // eslint-disable-line

  if (!open) return null;

  const send = async () => {
    if (!form.to || !form.subject) {
      toast.error('"To" and "Subject" are required');
      return;
    }
    setSending(true);
    try {
      if (initial.threadId) {
        await axios.post(`${API}/gmail/reply`, {
          threadId: initial.threadId,
          inReplyTo: initial.inReplyTo,
          to: form.to,
          subject: form.subject,
          body: form.body,
        }, { headers: authHdr() });
      } else {
        await axios.post(`${API}/gmail/send`, form, { headers: authHdr() });
      }
      toast.success('Email sent');
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" data-testid="compose-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900 text-lg">{initial.threadId ? 'Reply' : 'New message'}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="compose-close-btn">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <div>
            <label className="text-xs text-slate-500 uppercase font-semibold tracking-wider">To</label>
            <input
              type="email" value={form.to} onChange={e => setForm({ ...form, to: e.target.value })}
              data-testid="compose-to-input"
              placeholder="recipient@example.com" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Subject</label>
            <input
              type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
              data-testid="compose-subject-input"
              placeholder="Subject" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Message</label>
            <textarea
              rows={10} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
              data-testid="compose-body-input"
              placeholder="Write your message…" className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-sky-500 resize-none"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50 rounded-b-2xl">
          <span className="text-xs text-slate-500">Sent from your Google Workspace</span>
          <button
            onClick={send} disabled={sending}
            data-testid="compose-send-btn"
            className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 text-white font-semibold px-5 py-2 rounded-lg transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN PAGE ──────────────────────────────────────────────────────────────
const EmailPage = () => {
  const { user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState({ configured: false, linked: false, emailAddress: '' });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [folder, setFolder] = useState('INBOX');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeInitial, setComposeInitial] = useState({});

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(`${API}/gmail/status`, { headers: authHdr() });
      setStatus(res.data);
    } catch (e) {
      setStatus({ configured: false, linked: false, emailAddress: '' });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Show toast for OAuth result from query params
  useEffect(() => {
    const s = searchParams.get('gmail_status');
    if (s === 'connected') {
      toast.success('Gmail connected!');
      loadStatus();
      searchParams.delete('gmail_status');
      setSearchParams(searchParams, { replace: true });
    } else if (s === 'error') {
      toast.error(`Gmail connection failed: ${searchParams.get('detail') || 'unknown'}`);
      searchParams.delete('gmail_status');
      searchParams.delete('detail');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadStatus]);

  const loadInbox = useCallback(async () => {
    if (!status.linked) return;
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/gmail/inbox`, {
        params: { label: folder, q: search, max_results: 30 },
        headers: authHdr(),
      });
      setMessages(res.data.messages || []);
    } catch (e) {
      toast.error('Failed to load inbox');
    } finally { setLoadingList(false); }
  }, [folder, search, status.linked]);

  useEffect(() => { loadInbox(); }, [loadInbox]);

  const openMessage = async (id) => {
    setSelectedId(id);
    setLoadingMsg(true);
    try {
      const res = await axios.get(`${API}/gmail/message/${id}`, { headers: authHdr() });
      setSelectedMsg(res.data);
      // Mark as read if unread
      const m = messages.find(x => x.id === id);
      if (m && m.unread) {
        axios.post(`${API}/gmail/message/${id}/mark-read`, {}, { headers: authHdr() })
          .then(() => setMessages(prev => prev.map(x => x.id === id ? { ...x, unread: false } : x)))
          .catch(() => {});
      }
    } catch (e) {
      toast.error('Failed to open message');
    } finally { setLoadingMsg(false); }
  };

  const handleConnect = () => {
    const token = localStorage.getItem('crm_token');
    window.location.href = `${API}/gmail/oauth/start?token=${encodeURIComponent(token)}`;
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Gmail? You will need to re-link to read mail again.')) return;
    try {
      await axios.post(`${API}/gmail/disconnect`, {}, { headers: authHdr() });
      toast.success('Gmail disconnected');
      setStatus({ ...status, linked: false, emailAddress: '' });
      setMessages([]); setSelectedId(null); setSelectedMsg(null);
    } catch { toast.error('Failed to disconnect'); }
  };

  const handleReply = (msg) => {
    const fromEmail = parseFrom(msg.from).email;
    setComposeInitial({
      to: fromEmail,
      subject: msg.subject || '',
      body: `\n\n\n---\nOn ${msg.date}, ${msg.from} wrote:\n> ${(msg.text || msg.snippet || '').slice(0, 800).replace(/\n/g, '\n> ')}`,
      threadId: msg.threadId,
      inReplyTo: msg.id,
    });
    setComposeOpen(true);
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  if (loadingStatus) {
    return (
      <div className="p-12 flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-sky-600" />
      </div>
    );
  }

  if (!status.linked) {
    return (
      <div className="p-6 lg:p-8" data-testid="email-page">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
          <ConnectPrompt configured={status.configured} onConnect={handleConnect} />
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 lg:p-5 h-[calc(100vh-3rem)]" data-testid="email-page">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 h-full flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-4 lg:px-6 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-slate-900 truncate" style={{ fontFamily: 'Manrope' }}>Email</h1>
            <p className="text-[11px] text-slate-500 truncate">Linked: <span className="font-medium text-slate-700">{status.emailAddress}</span></p>
          </div>
          <button
            onClick={() => { setComposeInitial({}); setComposeOpen(true); }}
            data-testid="compose-new-btn"
            className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
            <Send className="w-3.5 h-3.5" /> Compose
          </button>
          <button onClick={loadInbox} className="p-1.5 hover:bg-slate-100 rounded-lg" title="Refresh">
            <RefreshCw className={`w-4 h-4 text-slate-600 ${loadingList ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={handleDisconnect} className="p-1.5 hover:bg-rose-50 rounded-lg" title="Disconnect" data-testid="gmail-disconnect-btn">
            <X className="w-4 h-4 text-rose-600" />
          </button>
        </div>

        {/* Body: folders | list | preview */}
        <div className="flex-1 flex overflow-hidden">
          {/* Folders */}
          <div className="hidden lg:block w-44 border-r border-slate-200 bg-slate-50/50 p-3">
            {FOLDERS.map(f => {
              const Icon = f.icon;
              return (
                <button
                  key={f.id}
                  onClick={() => { setFolder(f.id); setSelectedId(null); setSelectedMsg(null); }}
                  data-testid={`gmail-folder-${f.id.toLowerCase()}`}
                  className={`flex items-center gap-2 w-full px-3 py-2 mb-0.5 rounded-lg text-sm font-medium transition-colors ${
                    folder === f.id ? 'bg-sky-100 text-sky-800' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* Message list */}
          <div className={`${selectedId ? 'hidden lg:flex' : 'flex'} w-full lg:w-96 border-r border-slate-200 flex-col`}>
            {/* Search */}
            <div className="px-3 py-2 border-b border-slate-200">
              <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); }} className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search mail…"
                  data-testid="gmail-search-input"
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-slate-100 border border-transparent rounded-lg outline-none focus:bg-white focus:border-sky-400"
                />
              </form>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingList ? (
                <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-sky-600" /></div>
              ) : messages.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No messages</div>
              ) : (
                messages.map(m => {
                  const from = parseFrom(m.from);
                  return (
                    <button
                      key={m.id}
                      onClick={() => openMessage(m.id)}
                      data-testid={`gmail-msg-${m.id}`}
                      className={`flex flex-col items-start gap-0.5 w-full px-3 py-2.5 border-b border-slate-100 text-left hover:bg-slate-50 transition-colors ${
                        selectedId === m.id ? 'bg-sky-50' : ''
                      } ${m.unread ? 'bg-white' : 'bg-slate-50/40'}`}
                    >
                      <div className="flex items-center justify-between w-full gap-2">
                        <span className={`text-sm truncate ${m.unread ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                          {from.name || from.email}
                        </span>
                        <span className="text-[10px] text-slate-400 flex-shrink-0">{formatDate(m.date)}</span>
                      </div>
                      <div className={`text-xs truncate w-full ${m.unread ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                        {m.subject || '(no subject)'}
                      </div>
                      <div className="text-[11px] text-slate-400 truncate w-full">{m.snippet}</div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Preview */}
          <div className={`${selectedId ? 'flex' : 'hidden lg:flex'} flex-1 flex-col overflow-hidden`}>
            {!selectedMsg ? (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                <MailOpen className="w-12 h-12 mr-3 opacity-40" /> Select a message to read
              </div>
            ) : loadingMsg ? (
              <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>
            ) : (
              <>
                <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
                  <button onClick={() => { setSelectedId(null); setSelectedMsg(null); }} className="lg:hidden p-1.5 hover:bg-slate-100 rounded-lg">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-slate-900 text-base lg:text-lg break-words">{selectedMsg.subject || '(no subject)'}</h2>
                    <p className="text-xs text-slate-500 mt-1"><strong>{selectedMsg.from}</strong></p>
                    <p className="text-[11px] text-slate-400 mt-0.5">To: {selectedMsg.to} · {formatDate(selectedMsg.date)}</p>
                  </div>
                  <button
                    onClick={() => handleReply(selectedMsg)}
                    data-testid="reply-btn"
                    className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                    <Reply className="w-3.5 h-3.5" /> Reply
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                  {selectedMsg.html ? (
                    <div className="prose prose-sm max-w-none text-slate-800" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedMsg.html) }} />
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans">{selectedMsg.text || selectedMsg.snippet}</pre>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        initial={composeInitial}
        onSent={() => loadInbox()}
      />
    </div>
  );
};

export default EmailPage;
